// @flow
import * as React from 'react'
import { ConfigProvider, Spin, Icon, Input, notification } from 'antd'
import matchSorter, { rankings } from 'match-sorter'
import { IntlProvider, FormattedMessage } from 'react-intl'
import { geolocated, GeolocatedProps } from 'react-geolocated'
import { getDistance } from 'geolib'
import produce from 'immer'
import InstallPWA from '../InstallPWA'
import Listing from '../Listing'
import Filters from '../Filters'

import enUS from 'antd/es/locale/en_US'
import itIT from 'antd/es/locale/it_IT'
import 'moment/locale/it'

import it from '../../i18n/it.json'
import en from '../../i18n/en.json'

import { fetchTags, fetchEvents } from '../../utils/apiFetch'

const { Search } = Input

const messages = { it, en }

type State = {
  events: Array<Event>,
  locations: Array<Filter>,
  tags: {
    [string]: Array<Filter>,
  },
  loading: boolean,
  filters: {
    text: string,
    dates: {
      from: object | null,
      to: object | null,
    },
    locations: Array<string>,
    gpsLocation: boolean,
    tags: Array<string>,
    myFe: boolean,
    lang: 'it' | 'en',
  },
  filteredEvents: Array<Event>,
}

class ListingContainer extends React.Component<GeolocatedProps, State> {
  constructor(props) {
    super(props)

    const currentLang = localStorage.getItem('lang')

    this.state = {
      events: [],
      locations: [],
      tags: {
        it: [],
        en: [],
      },
      loading: true,
      filters: {
        text: '',
        dates: {
          from: null,
          to: null,
        },
        locations: [],
        gpsLocation: false,
        tags: [],
        myFe: false,
        lang: currentLang ? currentLang : 'it',
      },
      filteredEvents: [],
    }
  }

  componentDidMount() {
    // handle tags xml parsed data
    fetchTags()
      .then(rawData => {
        const tags = rawData.defaults.categorie.reduce((acc, el) => {
          acc[el._attributes.lang] = el.categoria.map(tag => ({
            value: tag._attributes.id,
            label: tag._cdata,
          }))
          return acc
        }, {})

        this.setState(
          {
            tags,
          },
          () => {
            // handle events xml parsed data
            this.loadEvents()
          },
        )
      })
      .catch(err => {
        console.error(err)
        notification.error({
          duration: null,
          message:
            this.state.filters.lang === 'it'
              ? 'Errore nel caricamento degli eventi'
              : 'Error while loading events',
          description:
            this.state.filters.lang === 'it'
              ? "Non è stato possibile contattare il server o c'è un errore nella risposta"
              : "It hasn't been possible to contact the server or it happened an error in the response",
        })
      })
  }

  loadEvents = (callBack: ?() => void) => {
    const { filters } = this.state
    console.log('Language: ', filters.lang)

    // dates allowed by API as 'dd/mm/YY'
    fetchEvents(
      filters.lang,
      Object.values(filters.dates).map(date =>
        date ? date.format('DD/MM/YY') : '',
      ),
    ).then(rawData => {
      let locations: { [string]: Filter } = {}
      let events: Array<Event> =
        rawData.eventi?.evento?.map(event => {
          const locationID = event._attributes.luogo
          const locationName = event.luogo._cdata

          if (!locations[locationID]) {
            locations[locationID] = {
              value: locationID,
              label: locationName,
            }
          }

          return {
            UID: event._attributes.id,
            tag: event._attributes.categoria,
            myfe: event._attributes.myfecard === '1',
            location: locationName,
            gpsLocation:
              event.lat && event.lng
                ? {
                    lat: event.lat._cdata,
                    lng: event.lng._cdata,
                  }
                : null,
            date: event.data._cdata,
            title: event.titolo._cdata,
            image: event.img._cdata,
          }
        }) ?? []

      this.setState(
        {
          events,
          filteredEvents: events,
          locations: Object.values(locations),
          loading: false,
        },
        () => {
          if (callBack) {
            callBack()
          }
        },
      )
    })
  }

  applyFilters = (filters: { [string]: Array<string> }): void => {
    this.setState(
      (prevState: State) => ({
        filters: {
          ...prevState.filters,
          ...filters,
        },
        loading: true,
      }),
      () => {
        console.log('Applying filters: ', filters)

        const evnPrc = () => {
          const filteredEvents = this.getFilteredEvents()
          this.setState({
            filteredEvents,
            loading: false,
          })
        }

        if (filters.lang) {
          localStorage.setItem('lang', filters.lang)
        }

        if (filters.dates || filters.lang) {
          this.loadEvents(evnPrc)
        } else {
          evnPrc()
        }
      },
    )
  }

  onSearchChange = e => {
    const text = e.target.value
    this.setState(
      produce(draft => {
        draft.filters.text = text
      }),
      () => {
        if (this.state.filters.text.legth === 0) {
          this.onSearch()
        }
      },
    )
  }

  onSearch = () => {
    this.setState(
      {
        loading: true,
      },
      () => {
        const filteredEvents = this.getFilteredEvents()
        this.setState({
          filteredEvents,
          loading: false,
        })
      },
    )
  }

  getFilteredEvents = () => {
    const { events, filters, locations, tags } = this.state
    const { isGeolocationEnabled, coords } = this.props
    let filteredEvents: Array<Event> = events
    console.log('geo: ', isGeolocationEnabled, coords)

    if (filters.myFe) {
      filteredEvents = filteredEvents.filter(ev => ev.myfe)
    }

    if (filters.tags.length > 0) {
      const filterTags = tags[filters.lang]
        .filter(tag => filters.tags.indexOf(tag.value) > -1)
        .map(tag => tag.value)
      filteredEvents = filteredEvents.filter(
        ev => filterTags.indexOf(ev.tag) > -1,
      )
    }

    if (filters.text.length > 0) {
      filteredEvents = matchSorter(filteredEvents, filters.text, {
        keys: ['title'],
        threshold: rankings.CONTAINS,
      })
    }

    if (filters.locations.length > 0) {
      const filterLocations = locations
        .filter(loc => filters.locations.indexOf(loc.value) > -1)
        .map(loc => loc.label)
      filteredEvents = filteredEvents.filter(
        ev => filterLocations.indexOf(ev.location) > -1,
      )
    }

    if (filters.gpsLocation && isGeolocationEnabled && coords) {
      filteredEvents = filteredEvents.filter(ev => {
        if (!ev.gpsLocation) return false
        const distance = getDistance(
          {
            latitude: ev.gpsLocation.lat,
            longitude: ev.gpsLocation.lng,
          },
          {
            latitude: coords.latitude,
            longitude: coords.longitude,
          },
        )

        // nearby => around me by 10km
        return distance < 10000
      })
    }

    return filteredEvents
  }

  render() {
    const { loading, locations, tags, filteredEvents } = this.state
    const { isGeolocationEnabled, coords } = this.props
    const language = this.state.filters.lang

    return (
      <IntlProvider locale={language} messages={messages[language]}>
        <ConfigProvider locale={language === 'it' ? itIT : enUS}>
          {loading ? (
            <div
              style={{
                height: 'calc(100vh - 5rem)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <FormattedMessage id="loading" defaultMessage="Loading...">
                {text => (
                  <Spin
                    tip={text}
                    indicator={
                      <Icon
                        type="loading"
                        style={{ fontSize: 24, marginBottom: '1rem' }}
                        spin
                      />
                    }
                  />
                )}
              </FormattedMessage>
            </div>
          ) : (
            <React.Fragment>
              <FormattedMessage id="search" defaultMessage="Search...">
                {text => (
                  <Search
                    id="search-events"
                    placeholder={text}
                    onSearch={this.onSearch}
                    onChange={this.onSearchChange}
                    value={this.state.filters.text}
                    allowClear
                    style={{
                      margin: '1rem 0.5rem',
                      width: 'calc(100% - 1rem)',
                    }}
                  />
                )}
              </FormattedMessage>
              <Listing events={filteredEvents} />
            </React.Fragment>
          )}
          <Filters
            setActiveFilters={this.applyFilters}
            locations={locations}
            isGeolocationEnabled={isGeolocationEnabled && coords}
            tags={tags[language]}
          />
          <InstallPWA />
        </ConfigProvider>
      </IntlProvider>
    )
  }
}

export default geolocated()(ListingContainer)
